import {skipToken} from '@reduxjs/toolkit/query'
import {useContext, useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import {CurrentUserKeyContext} from '../../../contexts/currentUserKey'
import {usePermission} from '../../../hooks/usePermission'
import type {User} from '../../../services/rest'
import {restApi} from '../../../services/rest'
import type {UserId} from '../../../types'
import {Permission, ROOT_PROJECT_ID} from '../../../types'
import {emptyArray} from '../../../utils/empty'

import type {AllUserSelectFragment$key} from './__generated__/AllUserSelectFragment.graphql'
import UserSelect from './UserSelect'
import {allUsersArg} from './UserSelect.rest'
import type {UserSelectData, UserSelectSelectedItem} from './UserSelect.types'
import {
  ALL_REGISTERED_USERS_LABEL,
  ALL_USERS_SELECT_ITEM,
  getSelectedItemByUserId,
  getSelectedItemByUserName,
  getSelectItem,
  sortUsersByName,
} from './UserSelect.utils'

type Props = {
  className?: string
  popupClassName?: string
  label?: string
  selected: UserId | string | null | undefined // userId or username
  withAllUsers?: boolean
  onChange: (selected: UserSelectSelectedItem) => unknown
}

const allUserSelectFragment = graphql`
  fragment AllUserSelectFragment on User {
    rawId
    avatars {
      urlToSize20
      urlToSize28
      urlToSize32
      urlToSize40
      urlToSize56
      urlToSize64
      urlToSize80
    }
    name
    username
  }
`

function AllUserSelect({
  className,
  label,
  popupClassName,
  selected,
  withAllUsers = true,
  onChange,
}: Props) {
  const hasPermission = usePermission(Permission.VIEW_USER_PROFILE, ROOT_PROJECT_ID)
  const {users, allUsersLoading} = restApi.endpoints.getAllUsers.useQuery(
    hasPermission ? allUsersArg : skipToken,
    {
      selectFromResult: ({data, isLoading}) => ({
        users: data?.user ?? emptyArray,
        allUsersLoading: isLoading,
      }),
    },
  )
  const currentUserKey: AllUserSelectFragment$key | null = useContext(CurrentUserKeyContext)
  const currentUser = useFragment(allUserSelectFragment, currentUserKey)
  const sortedUsers = useMemo(() => sortUsersByName(users), [users])
  const loading = allUsersLoading || currentUser == null

  const data: UserSelectData = useMemo(() => {
    let items: UserSelectData = withAllUsers ? [ALL_USERS_SELECT_ITEM] : []

    if (currentUser != null) {
      items.push(
        getSelectItem({user: {...currentUser, id: currentUser.rawId}, isCurrentUser: true}),
      )
    }
    if (sortedUsers.length > 0) {
      items.push(ALL_REGISTERED_USERS_LABEL)
      items = items.concat(
        sortedUsers
          .filter((user: User) => user.id !== currentUser?.rawId)
          .map((user: User) => getSelectItem({user})),
      )
    }
    return items
  }, [currentUser, sortedUsers, withAllUsers])

  const selectedItem = useMemo(
    () =>
      selected != null && selected !== ''
        ? getSelectedItemByUserId(data, selected) ??
          getSelectedItemByUserName(data, selected as string)
        : null,
    [data, selected],
  )

  return (
    <UserSelect
      className={className}
      data={data}
      label={label}
      popupClassName={popupClassName}
      selected={selectedItem}
      loading={loading}
      onChange={onChange}
    />
  )
}

export default AllUserSelect
